<template>
  <el-dialog
    title="企业微信托管"
    :visible.sync="dialogVisible"
    width="700px"
    @closed="handleClosed"
    append-to-body
  >
    <div v-loading="loading" class="login-info-wrap">
      <div v-if="boundDevices.length" class="bound-device-toolbar">
        <span class="bound-device-toolbar__label">当前设备</span>
        <el-select
          v-model="activeBoundDeviceId"
          filterable
          style="width: 360px"
          @change="handleBoundDeviceChange"
        >
          <el-option
            v-for="item in boundDevices"
            :key="item.deviceId"
            :label="getDeviceLabel(item)"
            :value="item.deviceId"
          />
        </el-select>
        <el-button icon="el-icon-plus" @click="handleAddDevice">添加设备</el-button>
      </div>

      <!-- 绑定状态 -->
      <div v-if="bindDevice" class="bind-status">
        <el-alert
          title="已绑定设备"
          type="success"
          :closable="false"
          show-icon
        />
        <el-alert
          v-if="profileSyncPending"
          :type="profileSyncTimedOut || bindDevice.profileSyncStatus === 'TIMEOUT' ? 'warning' : 'info'"
          :closable="false"
          show-icon
          style="margin-top: 12px"
        >
          <template slot="title">
            {{ profileSyncing ? '登录成功，正在同步企微资料…' : '登录成功，资料同步较慢，可稍后继续刷新' }}
            <el-button v-if="!profileSyncing" type="text" @click="startPollingBindStatus()">继续刷新</el-button>
          </template>
        </el-alert>
        <div class="device-info" style="margin-top: 20px">
          <el-descriptions :column="1" border>
            <el-descriptions-item label="设备名称">{{
              bindDevice.deviceName || bindDevice.deviceId || '未知设备'
            }}</el-descriptions-item>
            <el-descriptions-item label="企微昵称">{{
              profileSyncPending ? profileFieldPlaceholder : (bindDevice.wecomNickname || '-')
            }}</el-descriptions-item>
            <el-descriptions-item label="企微账号">{{
              profileSyncPending ? profileFieldPlaceholder : (bindDevice.phone || '-')
            }}</el-descriptions-item>
            <el-descriptions-item label="企微Id">{{
              profileSyncPending ? profileFieldPlaceholder : (bindDevice.wecomUserId || '-')
            }}</el-descriptions-item>
            <el-descriptions-item label="设备状态">
              <el-tag :type="bindDevice.online ? 'success' : 'info'">{{
                bindDevice.online ? '在线' : '离线'
              }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="企微状态">
              <el-tag :type="wecomStatusTagType">{{
                wecomStatusText
              }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </div>

        <!-- 通知配置 -->
        <div class="notify-config" style="margin-top: 20px">
          <el-card header="掉线通知配置" shadow="never">
            <el-form label-width="100px" size="small">
              <el-row :gutter="20">
                <el-col :span="12">
                  <el-form-item label="通知开关">
                    <el-switch
                      v-model="notifyConfig.notifyEnabled"
                      active-text="开启"
                      inactive-text="关闭"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="推送间隔">
                    <el-input-number
                      v-model="notifyConfig.notifyIntervalMinutes"
                      :min="5"
                      :max="120"
                      :step="5"
                      :controls="true"
                      controls-position="right"
                      class="readonly-input-number"
                      style="width: 120px"
                      @keydown.native.prevent
                      @mousewheel.native.prevent
                    />
                    <span style="margin-left: 8px; color: #909399; font-size: 12px">分钟</span>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="通知时间段">
                <el-time-picker
                  v-model="notifyConfig.notifyStartTime"
                  format="HH:mm"
                  value-format="HH:mm"
                  placeholder="开始"
                  style="width: 110px"
                  size="small"
                />
                <span style="margin: 0 8px">至</span>
                <el-time-picker
                  v-model="notifyConfig.notifyEndTime"
                  format="HH:mm"
                  value-format="HH:mm"
                  placeholder="结束"
                  style="width: 110px"
                  size="small"
                />
                <el-button type="primary" size="mini" :loading="saveNotifyLoading" @click="handleSaveNotifyConfig" style="margin-left: 10px">
                  保存
                </el-button>
              </el-form-item>
              <div style="font-size: 12px; color: #909399; line-height: 1.4; padding-left: 100px">
                说明：关闭通知或在非通知时间段内，企微掉线将不会推送消息提醒
              </div>
            </el-form>
          </el-card>
        </div>

        <div v-if="false" class="reset-tip" style="margin-top: 20px; padding: 12px; background-color: #E6F7FF; border: 1px solid #91D5FF; border-radius: 4px; color: #0050B3; font-size: 13px; line-height: 1.6">
          <div style="margin-bottom: 4px">若您非自己操作掉线，请先点击【掉线优化】后再点击【重新绑定】扫码上线企微</div>
          <div>优化流程大约需要3-5分钟，优化后重新登录企微，可有效改善掉线情况！</div>
        </div>
        <div class="actions" style="margin-top: 12px; text-align: center">
          <el-button
            v-if="false"
            type="primary"
            icon="el-icon-refresh"
            :loading="resetLoading"
            :disabled="resetLoading || rebindLoading"
            @click="handleResetDevice"
          >{{ isResetting ? '优化中（点击查看进度）' : '掉线优化' }}</el-button>
          <el-button :loading="rebindLoading" :disabled="isResetting" @click="handleRebind">{{ rebindLoading ? '正在检测企微状态' : '重新绑定' }}</el-button>
          <el-button :loading="screenshotLoading" :disabled="isResetting" type="success" @click="handleScreenshot">设备截屏</el-button>
        </div>
      </div>

      <!-- 未绑定状态 -->
      <div
        v-else
        class="unbind-status"
        style="text-align: center; padding: 40px 0"
      >
        <i
          class="el-icon-mobile-phone"
          style="font-size: 60px; color: #909399; margin-bottom: 20px"
        />
        <template v-if="selectedDeviceId">
          <div style="font-size: 16px; color: #606266; margin-bottom: 12px">请选择需要绑定的云手机</div>
          <el-select v-model="selectedDeviceId" filterable style="width: 360px; margin-bottom: 16px">
            <el-option
              v-for="item in unboundDevices"
              :key="item.deviceId"
              :label="item.deviceName || item.deviceId"
              :value="item.deviceId"
              :disabled="!item.online"
            />
          </el-select>
          <div v-if="false" class="reset-tip" style="margin-bottom: 12px; padding: 12px; background-color: #E6F7FF; border: 1px solid #91D5FF; border-radius: 4px; color: #0050B3; font-size: 13px; line-height: 1.6; text-align: left">
            <div style="margin-bottom: 4px">若您非自己操作掉线，请先点击【掉线优化】后再点击【重新绑定】扫码上线企微</div>
            <div>优化流程大约需要3-5分钟，优化后重新登录企微，可有效改善掉线情况！</div>
          </div>
          <div class="actions unbound-reset-actions">
            <el-button
              v-if="false"
              type="primary"
              icon="el-icon-refresh"
              :loading="resetLoading"
              :disabled="resetLoading"
              @click="handleResetDevice"
            >{{ isResetting ? '优化中（点击查看进度）' : '掉线优化' }}</el-button>
            <el-button :disabled="isResetting" @click="handleBind">立即绑定</el-button>
            <el-button v-if="boundDevices.length" @click="handleBackToBoundDevice">返回</el-button>
          </div>
        </template>
        <template v-else-if="noExtraDevice">
          <div style="font-size: 16px; color: #606266; margin-bottom: 30px">
            没有多余的云手机进行绑定
          </div>
          <el-button v-if="boundDevices.length" @click="handleBackToBoundDevice">返回已绑定设备</el-button>
        </template>
        <template v-else-if="noExtraDeviceno">
          <div style="font-size: 16px; color: #606266; margin-bottom: 30px">
            云手机不在线
          </div>
          <el-button v-if="boundDevices.length" @click="handleBackToBoundDevice">返回已绑定设备</el-button>
        </template>
        <template v-else>
          <div style="font-size: 16px; color: #606266; margin-bottom: 30px">
            暂未绑定企业微信托管云手机设备
          </div>
          <el-button type="primary" @click="handleBind">立即绑定</el-button>
        </template>
      </div>
    </div>

    <!-- 微信登录弹窗 -->
    <WxLogin
      :visible.sync="wxLoginVisible"
      :deviceId="selectedDeviceId"
      @loginSuccess="handleLoginSuccess"
    />

    <!-- 掉线优化进度弹窗 -->
    <el-dialog v-if="false"
      :title="resetModalTitle"
      :visible.sync="resetModalVisible"
      width="500px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      append-to-body
    >
      <div class="reset-modal-content">
        <!-- 进行中状态 -->
        <template v-if="resetProgress && resetProgress.status === 'RUNNING'">
          <div class="reset-progress-tip" style="margin-bottom: 16px; text-align: center; color: #606266; font-size: 14px">
            掉线优化进行中，可关闭弹窗后台运行，请优化完毕后再登录
          </div>
          <el-progress
            :percentage="Number(resetProgress.percent || 0)"
            :status="resetProgressStatus"
            :format="formatResetProgress"
          />
          <!-- <div class="reset-progress-stage" style="margin-top: 16px; text-align: center; color: #909399; font-size: 13px">
            {{ resetProgress.stage || '等待优化完毕' }}<span v-if="resetProgress.message">：{{ resetProgress.message }}</span>
          </div>
 -->
          <div class="reset-progress-estimate" style="margin-top: 8px; text-align: center; color: #C0C4CC; font-size: 12px">
            预计 3-5 分钟
          </div>
        </template>

        <!-- 失败状态 -->
        <template v-if="resetProgress && resetProgress.status === 'FAILED'">
          <el-alert
            title="抱歉，掉线优化处理失败，请点击重试按钮重启优化"
            type="error"
            :closable="false"
            show-icon
          />
        </template>

        <!-- 成功状态 -->
        <template v-if="resetProgress && resetProgress.status === 'SUCCESS'">
          <el-result icon="success" title="优化处理完毕！" />
        </template>
      </div>

      <span slot="footer" class="dialog-footer">
        <!-- 进行中：仅显示关闭按钮 -->
        <el-button v-if="resetProgress && resetProgress.status === 'RUNNING'" @click="resetModalVisible = false">
          关闭
        </el-button>

        <!-- 失败：显示重新优化按钮 -->
        <el-button
          v-if="resetProgress && resetProgress.status === 'FAILED'"
          type="warning"
          icon="el-icon-refresh"
          :loading="resetLoading"
          @click="handleRetryReset"
        >
          重试
        </el-button>

        <!-- 成功：显示关闭按钮 -->
        <el-button v-if="resetProgress && resetProgress.status === 'SUCCESS'" type="primary" @click="resetModalVisible = false">
          关闭
        </el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import WxLogin from './wxLogin.vue'
import {
  getBindStatus,
  startRebindProbe,
  getRebindProbe,
  getUnBindList,
  saveNotifyConfig,
  resetDevice,
  getDeviceResetProgress
} from '@/api/smartSalesPhone/cloudPhone'
import { captureScreenshot, getScreenshotResult } from '@/api/smartSalesPhone/screenshot'
import { getBoundDeviceLabel, normalizeBoundDevices } from '@/utils/cloudPhoneBinding'

export default {
  name: 'CrmLoginInfo',
  components: {
    WxLogin
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      bindDevice: null,
      boundDevices: [],
      unboundDevices: [],
      activeBoundDeviceId: '',
      addingDevice: false,
      wxLoginVisible: false,
      selectedDeviceId: '',
      noExtraDevice: false,
      noExtraDeviceno: false,
      pollTimer: null,
      bindPollRequestId: 0,
      bindInfoRequestId: 0,
      profileSyncing: false,
      profileSyncTimedOut: false,
      screenshotPollTimer: null,
      screenshotLoading: false,
      saveNotifyLoading: false,
      resetLoading: false,
      rebindLoading: false,
      rebindRequestId: 0,
      resetProgress: null,
      resetProgressTimer: null,
      resetModalVisible: false,
      notifyConfig: {
        notifyEnabled: true,
        notifyStartTime: '00:00',
        notifyEndTime: '23:59',
        notifyIntervalMinutes: 15
      }
    }
  },
  computed: {
    profileSyncPending() {
      return this.profileSyncing || this.profileSyncTimedOut || (this.bindDevice &&
        ['SYNCING', 'TIMEOUT'].includes(this.bindDevice.profileSyncStatus))
    },
    profileFieldPlaceholder() {
      return this.profileSyncing ? '同步中…' : '待同步'
    },
    wecomStatusText() {
      if (!this.bindDevice || typeof this.bindDevice.wecomOnline !== 'boolean') return '状态未知'
      return this.bindDevice.wecomOnline ? '在线' : '已掉线'
    },
    wecomStatusTagType() {
      if (!this.bindDevice || typeof this.bindDevice.wecomOnline !== 'boolean') return 'info'
      return this.bindDevice.wecomOnline ? 'success' : 'danger'
    },
    isResetting() {
      return this.resetLoading || (this.resetProgress && this.resetProgress.status === 'RUNNING')
    },
    resetProgressStatus() {
      if (!this.resetProgress) return undefined
      if (this.resetProgress.status === 'SUCCESS') return 'success'
      if (this.resetProgress.status === 'FAILED') return 'exception'
      return undefined
    },
    resetProgressLabel() {
      const labels = { RUNNING: '进行中', SUCCESS: '已完成', FAILED: '失败' }
      return labels[this.resetProgress && this.resetProgress.status] || '等待中'
    },
    resetProgressTagType() {
      const types = { RUNNING: 'warning', SUCCESS: 'success', FAILED: 'danger' }
      return types[this.resetProgress && this.resetProgress.status] || 'info'
    },
    resetModalTitle() {
      if (!this.resetProgress) return '掉线优化'
      if (this.resetProgress.status === 'RUNNING') return '掉线优化进行中'
      if (this.resetProgress.status === 'FAILED') return '掉线优化处理失败！'
      if (this.resetProgress.status === 'SUCCESS') return '掉线优化处理完成！'
      return '掉线优化'
    }
  },
  watch: {
    dialogVisible(visible) {
      if (!visible) {
        this.cancelRebindProbe()
        this.clearPollTimer()
        this.bindInfoRequestId++
      }
    }
  },
  beforeDestroy() {
    this.cancelRebindProbe()
    this.clearPollTimer()
    this.clearScreenshotPoll()
    this.clearResetProgressTimer()
    this.bindInfoRequestId++
  },
  methods: {
    show() {
      this.clearPollTimer()
      this.dialogVisible = true
      this.addingDevice = false
      this.fetchBindInfo()
    },
    async fetchUnBindList() {
      this.loading = true
      this.resetProgress = null
      try {
        const res = await getUnBindList()
        if (res.code === 1 || res.code === 200) {
          // 获取已绑定设备的 deviceId 集合
          const boundDeviceIds = new Set(this.boundDevices.map(d => d.deviceId))

          // 过滤掉已绑定的设备
          const allDevices = Array.isArray(res.data) ? res.data : []
          this.unboundDevices = allDevices.filter(device => !boundDeviceIds.has(device.deviceId))

          if (this.unboundDevices.length > 0) {
            this.noExtraDevice = false
            // 优先选择在线的设备
            this.selectedDeviceId =
              this.unboundDevices.find(item => item.online)?.deviceId || ''
            if (this.selectedDeviceId) {
              this.noExtraDevice = false
              this.noExtraDeviceno = false
              this.fetchResetProgress(this.selectedDeviceId).then(() => {
                if (this.resetProgress && this.resetProgress.status === 'RUNNING') {
                  this.startResetProgressPolling(this.selectedDeviceId)
                }
              })
              return
            }
            // 所有未绑定的设备都不在线
            this.noExtraDeviceno = true
          } else {
            // 没有可用的未绑定设备
            this.selectedDeviceId = ''
            this.noExtraDevice = true
            this.noExtraDeviceno = false
          }
        }
      } catch (error) {
        console.error('获取未绑定设备列表失败', error)
      } finally {
        this.loading = false
      }
    },
    async fetchBindInfo() {
      const requestId = ++this.bindInfoRequestId
      this.loading = true
      try {
        const res = await getBindStatus()
        if (requestId !== this.bindInfoRequestId || !this.dialogVisible) return
        if (res.code === 1 || res.code === 200) {
          this.boundDevices = normalizeBoundDevices(res.data)
          if (!this.boundDevices.length) {
            this.activeBoundDeviceId = ''
            await this.fetchUnBindList()
            this.bindDevice = null
          } else {
            const current = this.boundDevices.find(
              item => item.deviceId === this.activeBoundDeviceId
            )
            const selected = current || this.boundDevices[0]
            this.activeBoundDeviceId = selected.deviceId
            this.selectedDeviceId = selected.deviceId
            this.bindDevice = selected
            this.addingDevice = false
            if (selected.profileSyncStatus === 'SYNCING') {
              this.startPollingBindStatus()
            }
            this.fetchResetProgress(selected.deviceId).then(() => {
              if (this.resetProgress && this.resetProgress.status === 'RUNNING') {
                this.startResetProgressPolling(selected.deviceId)
              }
            })
            // 加载通知配置
            if (res.data.notifyEnabled !== undefined) {
              this.notifyConfig = {
                notifyEnabled: res.data.notifyEnabled,
                notifyStartTime: res.data.notifyStartTime || '00:00',
                notifyEndTime: res.data.notifyEndTime || '23:59',
                notifyIntervalMinutes: res.data.notifyIntervalMinutes || 15
              }
            }
          }
        }
      } catch (error) {
        console.error('获取设备信息失败', error)
      } finally {
        if (requestId === this.bindInfoRequestId) this.loading = false
      }
    },
    handleBind() {
      if (!this.selectedDeviceId) {
        this.$message.warning('请选择在线云手机')
        return
      }
      this.wxLoginVisible = true
    },
    cancelRebindProbe() {
      this.rebindRequestId++
      this.rebindLoading = false
    },
    async handleRebind() {
      if (this.rebindLoading || this.isResetting) return
      const deviceId = this.bindDevice && this.bindDevice.deviceId
      if (!deviceId) {
        this.$message.error('设备信息不存在，请刷新后重试')
        return
      }
      const requestId = ++this.rebindRequestId
      const isCurrent = () => requestId === this.rebindRequestId && this.dialogVisible &&
        this.bindDevice && this.bindDevice.deviceId === deviceId
      this.rebindLoading = true
      try {
        const res = await startRebindProbe()
        if (!isCurrent()) return
        if ((res.code !== 1 && res.code !== 200) || !res.data || !res.data.probeId) {
          throw new Error(res.message || '企微状态探测下发失败，请稍后重试')
        }
        const probeId = res.data.probeId
        const deadline = Date.now() + 30000
        while (Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 1000))
          if (!isCurrent()) return
          const result = await getRebindProbe(probeId)
          if (!isCurrent()) return
          if ((result.code !== 1 && result.code !== 200) || !result.data) {
            throw new Error(result.message || '获取企微状态失败，请稍后重试')
          }
          const state = result.data
          if (state.deviceId !== deviceId) {
            throw new Error('绑定设备已变更，请刷新后重试')
          }
          if (state.status === 'PENDING') continue

          // 探测失败或超时时，给用户选择权，不阻止重新绑定
          if (state.status === 'FAILED' || state.status === 'TIMEOUT') {
            try {
              await this.$confirm(
                '企微状态探测失败，无法确定当前登录状态。是否继续重新绑定？',
                '探测失败',
                {
                  type: 'warning',
                  confirmButtonText: '继续绑定',
                  cancelButtonText: '取消'
                }
              )
              if (isCurrent() && !this.isResetting) {
                this.selectedDeviceId = deviceId
                this.wxLoginVisible = true
              }
              return
            } catch (e) {
              return  // 用户取消
            }
          }

          if (state.status !== 'SUCCESS' || typeof state.wecomOnline !== 'boolean') {
            throw new Error(state.message || '企微状态探测异常，请稍后重试')
          }
          this.$set(this.bindDevice, 'wecomOnline', state.wecomOnline)
          if (state.wecomOnline) {
            try {
              await this.$confirm(
                '当前企微是在线状态，是否确认还需要重新绑定？',
                '确认重新绑定',
                { type: 'warning', confirmButtonText: '确认重新绑定', cancelButtonText: '取消' }
              )
            } catch (e) {
              return
            }
          }
          if (isCurrent() && !this.isResetting) {
            this.selectedDeviceId = deviceId
            this.wxLoginVisible = true
          }
          return
        }
        // 前端轮询超时，也给用户选择权
        try {
          await this.$confirm(
            '企微状态探测超时，无法确定当前登录状态。是否继续重新绑定？',
            '探测超时',
            {
              type: 'warning',
              confirmButtonText: '继续绑定',
              cancelButtonText: '取消'
            }
          )
          if (isCurrent() && !this.isResetting) {
            this.selectedDeviceId = deviceId
            this.wxLoginVisible = true
          }
          return
        } catch (e) {
          return  // 用户取消
        }
      } catch (error) {
        if (isCurrent()) this.$message.error(error.message || '企微状态探测失败，请稍后重试')
      } finally {
        if (requestId === this.rebindRequestId) this.rebindLoading = false
      }
    },
    getDeviceLabel(device) {
      if (['SYNCING', 'TIMEOUT'].includes(device.profileSyncStatus)) {
        const status = device.profileSyncStatus === 'SYNCING' ? '资料同步中' : '资料待同步'
        return `${device.deviceName || device.deviceId}（${status}）`
      }
      return getBoundDeviceLabel(device)
    },
    handleBoundDeviceChange(deviceId) {
      const device = this.boundDevices.find(item => item.deviceId === deviceId)
      if (!device) return
      this.clearPollTimer()
      this.cancelRebindProbe()
      this.bindInfoRequestId++
      this.clearResetProgressTimer()
      this.resetProgress = null
      this.addingDevice = false
      this.bindDevice = device
      this.activeBoundDeviceId = deviceId
      this.selectedDeviceId = deviceId
      this.loading = false
      if (device.profileSyncStatus === 'SYNCING') this.startPollingBindStatus()
      this.fetchResetProgress(deviceId).then(() => {
        if (this.resetProgress && this.resetProgress.status === 'RUNNING') {
          this.startResetProgressPolling(deviceId)
        }
      })
    },
    async handleAddDevice() {
      this.clearPollTimer()
      this.cancelRebindProbe()
      this.bindInfoRequestId++
      this.clearResetProgressTimer()
      this.resetProgress = null
      this.addingDevice = true
      this.bindDevice = null
      await this.fetchUnBindList()
    },
    handleBackToBoundDevice() {
      const deviceId = this.activeBoundDeviceId || this.boundDevices[0]?.deviceId
      if (deviceId) this.handleBoundDeviceChange(deviceId)
    },
    async handleResetDevice() {
      if (this.rebindLoading) return
      const deviceId = (this.bindDevice && this.bindDevice.deviceId) || this.selectedDeviceId
      if (!deviceId) {
        this.$message.error('未找到可优化的设备')
        return
      }

      // 防止按钮状态更新延迟或连续点击导致重复提交优化任务
      if (this.resetLoading) {
        return
      }

      // 如果已经在优化中，直接打开弹窗显示进度
      if (this.resetProgress && this.resetProgress.status === 'RUNNING') {
        this.resetModalVisible = true
        return
      }

      try {
        await this.$confirm(
          '掉线优化时间预计3-5分钟左右，后台将自动运行优化，您可以关闭窗口等待。优化完成后，请点击【重新绑定】重新扫码即可。',
          '确认掉线优化',
          { type: 'warning', confirmButtonText: '确定优化', cancelButtonText: '取消' }
        )
      } catch (e) {
        return
      }

      this.resetLoading = true
      try {
        const res = await resetDevice({ deviceId })
        if (res.code !== 200 && res.code !== 1) {
          throw new Error(res.message || '掉线优化提交失败')
        }
        this.resetProgress = res.data || {
          deviceId,
          status: 'RUNNING',
          percent: 0,
          stage: '排队中',
          message: '优化任务已提交'
        }
        this.startResetProgressPolling(deviceId)
        this.resetModalVisible = true
        this.$message.success('掉线优化任务已提交')
      } catch (error) {
        this.$message.error(error.message || '掉线优化提交失败')
      } finally {
        this.resetLoading = false
      }
    },
    async handleRetryReset() {
      this.resetModalVisible = false
      await this.handleResetDevice()
    },
    async fetchResetProgress(deviceId) {
      if (!deviceId) return
      try {
        const res = await getDeviceResetProgress(deviceId)
        if (res.code === 200 || res.code === 1) {
          const prevStatus = this.resetProgress?.status
          this.resetProgress = res.data || null

          // 如果状态从 RUNNING 变为 SUCCESS 或 FAILED，且弹窗已关闭，则自动打开弹窗
          if (prevStatus === 'RUNNING' && this.resetProgress &&
              (this.resetProgress.status === 'SUCCESS' || this.resetProgress.status === 'FAILED') &&
              !this.resetModalVisible) {
            this.resetModalVisible = true
          }
        }
      } catch (error) {
        console.error('查询掉线优化进度失败', error)
      }
    },
    startResetProgressPolling(deviceId) {
      this.clearResetProgressTimer()
      const poll = async () => {
        this.resetProgressTimer = null
        if (!this.dialogVisible) return
        await this.fetchResetProgress(deviceId)
        if (this.resetProgress && this.resetProgress.status === 'RUNNING') {
          this.resetProgressTimer = setTimeout(poll, 2000)
        }
      }
      this.resetProgressTimer = setTimeout(poll, 1000)
    },
    clearResetProgressTimer() {
      if (this.resetProgressTimer) {
        clearTimeout(this.resetProgressTimer)
        this.resetProgressTimer = null
      }
    },
    formatResetProgress(percentage) {
      return `${percentage}%`
    },
    formatResetTime(timestamp) {
      const date = new Date(timestamp)
      return isNaN(date.getTime()) ? '-' : date.toLocaleString()
    },
    async handleSaveNotifyConfig() {
      // 验证时间范围
      if (this.notifyConfig.notifyStartTime >= this.notifyConfig.notifyEndTime) {
        this.$message.error('开始时间必须早于结束时间')
        return
      }

      // 验证推送间隔
      if (this.notifyConfig.notifyIntervalMinutes < 5 || this.notifyConfig.notifyIntervalMinutes > 120) {
        this.$message.error('推送间隔必须在5-120分钟之间')
        return
      }

      this.saveNotifyLoading = true
      try {
        const res = await saveNotifyConfig({
          notifyEnabled: this.notifyConfig.notifyEnabled,
          notifyStartTime: this.notifyConfig.notifyStartTime,
          notifyEndTime: this.notifyConfig.notifyEndTime,
          notifyIntervalMinutes: this.notifyConfig.notifyIntervalMinutes
        })
        if (res.code === 1 || res.code === 200) {
          this.$message.success('保存成功')
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存通知配置失败', error)
        this.$message.error('保存失败')
      } finally {
        this.saveNotifyLoading = false
      }
    },
    async handleScreenshot() {
      if (this.screenshotLoading) return
      if (!this.bindDevice || !this.bindDevice.deviceId) {
        this.$message.error('设备信息不存在')
        return
      }

      if (!this.bindDevice.online) {
        this.$message.warning('设备离线，无法截屏')
        return
      }

      this.screenshotLoading = true

      try {
        // 1. 触发截屏
        const captureRes = await captureScreenshot(this.bindDevice.deviceId)
        if (captureRes.code !== 200 && captureRes.code !== 1) {
          throw new Error(captureRes.message || '截屏请求失败')
        }

        const taskId = captureRes.data.task_id
        if (!taskId) {
          throw new Error('未获取到任务ID')
        }

        // 2. 轮询获取截图结果（最多等待30秒）
        let retryCount = 0
        const maxRetries = 30
        const pollInterval = 1000

        const checkScreenshot = async () => {
          this.screenshotPollTimer = null
          try {
            const resultRes = await getScreenshotResult(taskId)
            // The dialog may have been closed while the request was pending.
            if (!this.screenshotLoading) return

            if (resultRes.code === 200 || resultRes.code === 1) {
              // 截图成功，获取base64并在新窗口中显示
              const screenshotData = resultRes.data

              if (screenshotData && screenshotData.success && screenshotData.png_base64) {
                // 将base64转换为可显示的图片URL
                const imageUrl = `data:image/png;base64,${screenshotData.png_base64}`

                // 在新窗口中显示图片
                const newWindow = window.open('', '_blank')
                if (newWindow) {
                  newWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>设备截屏 - ${this.bindDevice.deviceId}</title>
                      <style>
                        body { margin: 0; padding: 20px; background: #f5f5f5; text-align: center; }
                        img { max-width: 100%; height: auto; box-shadow: 0 2px 12px rgba(0,0,0,0.1); }
                        .info { margin-bottom: 10px; color: #666; }
                      </style>
                    </head>
                    <body>
                      <div class="info">设备: ${this.bindDevice.deviceId} | 尺寸: ${screenshotData.width}x${screenshotData.height}</div>
                      <img src="${imageUrl}" alt="设备截屏" />
                    </body>
                    </html>
                  `)
                  newWindow.document.close()
                }

                this.screenshotLoading = false
                this.$message.success('截屏成功')
              } else {
                throw new Error(screenshotData.error || '截图失败')
              }
            } else {
              // 截图还未完成或失败，继续轮询
              retryCount++
              if (retryCount < maxRetries) {
                this.screenshotPollTimer = setTimeout(checkScreenshot, pollInterval)
              } else {
                this.screenshotLoading = false
                this.$message.error('截屏超时，请稍后重试')
              }
            }
          } catch (error) {
            if (!this.screenshotLoading) return
            // 网络错误或其他异常，继续重试
            retryCount++
            if (retryCount < maxRetries) {
              this.screenshotPollTimer = setTimeout(checkScreenshot, pollInterval)
            } else {
              this.screenshotLoading = false
              this.$message.error('截屏超时，请稍后重试')
            }
          }
        }

        // 延迟2秒后开始检查（给设备截屏时间）
        this.screenshotPollTimer = setTimeout(checkScreenshot, 2000)
      } catch (error) {
        if (!this.screenshotLoading) return
        this.screenshotLoading = false
        console.error('截屏失败', error)
        this.$message.error(error.message || '截屏失败')
      }
    },
    handleLoginSuccess(result = {}) {
      this.wxLoginVisible = false
      if (!this.dialogVisible) return
      this.bindInfoRequestId++
      const deviceId = result.deviceId || this.selectedDeviceId
      const device = this.boundDevices.find(item => item.deviceId === deviceId) ||
        this.unboundDevices.find(item => item.deviceId === deviceId) || { deviceId }
      const pending = {
        ...device,
        loginId: result.loginId,
        wecomNickname: '',
        phone: '',
        wecomUserId: '',
        online: true,
        wecomOnline: true,
        profileSyncStatus: 'SYNCING'
      }
      this.boundDevices = [...this.boundDevices.filter(item => item.deviceId !== deviceId), pending]
      this.bindDevice = pending
      this.activeBoundDeviceId = deviceId
      this.selectedDeviceId = deviceId
      this.addingDevice = false
      this.loading = false
      this.startPollingBindStatus()
    },
    handleClosed() {
      this.bindDevice = null
      this.boundDevices = []
      this.unboundDevices = []
      this.activeBoundDeviceId = ''
      this.addingDevice = false
      this.clearPollTimer()
      this.clearScreenshotPoll()
      this.clearResetProgressTimer()
    },
    clearScreenshotPoll() {
      if (this.screenshotPollTimer) {
        clearTimeout(this.screenshotPollTimer)
        this.screenshotPollTimer = null
      }
      this.screenshotLoading = false
    },
    clearPollTimer() {
      this.bindPollRequestId++
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
      this.profileSyncing = false
      this.profileSyncTimedOut = false
    },
    startPollingBindStatus() {
      this.clearPollTimer()
      const deviceId = this.bindDevice && this.bindDevice.deviceId
      if (!deviceId || !this.dialogVisible) return
      const loginId = this.bindDevice.loginId
      const requestId = this.bindPollRequestId
      const deadline = Date.now() + 60000
      const isCurrent = () => requestId === this.bindPollRequestId && this.dialogVisible &&
        !this.addingDevice && this.activeBoundDeviceId === deviceId
      this.profileSyncing = true
      const poll = async () => {
        if (!isCurrent()) return
        if (Date.now() >= deadline) {
          this.profileSyncing = false
          this.profileSyncTimedOut = true
          this.pollTimer = null
          return
        }
        try {
          const res = await getBindStatus(undefined, { timeout: Math.min(10000, deadline - Date.now()) })
          if (!isCurrent()) return
          if (res.code === 1 || res.code === 200) {
            const devices = normalizeBoundDevices(res.data)
            const selected = devices.find(item => item.deviceId === deviceId)
            // 绑定记录可能早于本次登录资料可见，不能用旧登录的完整资料结束轮询。
            if (selected && (!loginId || selected.loginId === loginId)) {
              this.boundDevices = devices
              this.bindDevice = selected
              if (selected.profileSyncStatus === 'READY') {
                this.clearPollTimer()
                return
              }
            }
          }
        } catch (error) {
          console.error('轮询获取设备信息失败', error)
        }
        // 串行查询，避免慢请求重叠；过期请求不再安排下一轮。
        if (isCurrent()) this.pollTimer = setTimeout(poll, 1500)
      }
      poll()
    }
  }
}
</script>

<style scoped>
.login-info-wrap {
  min-height: 200px;
}

.bound-device-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.bound-device-toolbar__label {
  flex: 0 0 auto;
  color: #606266;
  font-size: 14px;
}

.reset-modal-content {
  min-height: 100px;
  padding: 20px 0;
}

.reset-progress-stage {
  color: #606266;
  font-size: 14px;
  text-align: center;
}

/* 禁用 el-input-number 的手动输入，只允许通过加减按钮调整 */
.readonly-input-number >>> .el-input__inner {
  pointer-events: none;
  cursor: default;
}

.readonly-input-number >>> .el-input-number__increase,
.readonly-input-number >>> .el-input-number__decrease {
  pointer-events: auto;
  cursor: pointer;
}
</style>
